import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Package, Bell, Phone, Building2, Calendar, Clock, Mail, CalendarPlus, Edit, Trash2 } from "lucide-react";
import { categoryIcons, getExpiryStatus, formatSerialDate } from "./serialIcons";

export function SerialNumberCard({
  serial,
  smtpConfigured,
  onEdit,
  onDelete,
  onSendEmail,
  onDownloadCalendar,
}) {
  const expiryStatus = getExpiryStatus(serial.expiry_date);
  const CategoryIcon = categoryIcons[serial.category] || Package;

  return (
    <Card className="card-hover" data-testid={`serial-card-${serial.id}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              expiryStatus === "expired" ? "bg-red-100 dark:bg-red-900/30" :
              expiryStatus === "expiring" ? "bg-amber-100 dark:bg-amber-900/30" :
              "bg-emerald-100 dark:bg-emerald-900/30"
            }`}>
              <CategoryIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${
                expiryStatus === "expired" ? "text-red-600 dark:text-red-400" :
                expiryStatus === "expiring" ? "text-amber-600 dark:text-amber-400" :
                "text-emerald-600 dark:text-emerald-400"
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg font-mono break-all leading-tight">{serial.serial_number}</h3>
              <p className="text-muted-foreground flex items-start gap-2 mt-1 min-w-0">
                <Package className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2 break-words">{serial.product_name}</span>
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {serial.category && (
                  <Badge variant="outline" className="gap-1">
                    <CategoryIcon className="w-3 h-3" />
                    {serial.category}
                  </Badge>
                )}
                {serial.customer_name && (
                  <Badge variant="secondary" className="gap-1">
                    <Building2 className="w-3 h-3" />
                    {serial.customer_name}
                  </Badge>
                )}
                {serial.purchase_date && (
                  <Badge variant="outline" className="gap-1">
                    <Calendar className="w-3 h-3" />
                    Kauf: {formatSerialDate(serial.purchase_date)}
                  </Badge>
                )}
                {serial.expiry_date && (
                  <Badge
                    variant={expiryStatus === "expired" ? "destructive" : expiryStatus === "expiring" ? "default" : "outline"}
                    className="gap-1"
                  >
                    <Clock className="w-3 h-3" />
                    Ablauf: {formatSerialDate(serial.expiry_date)}
                  </Badge>
                )}
              </div>
              {serial.customer_phone && expiryStatus && expiryStatus !== "valid" && (
                <a
                  href={`tel:${serial.customer_phone}`}
                  className="flex items-center gap-2 mt-3 text-sm text-primary hover:underline min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{serial.customer_phone} anrufen</span>
                </a>
              )}
              {serial.notes && <p className="text-sm text-muted-foreground mt-2 line-clamp-2 break-words">{serial.notes}</p>}
            </div>
          </div>
          <div className="flex items-stretch sm:items-center justify-end gap-2 sm:gap-1 shrink-0 mt-1 pt-3 border-t border-border sm:mt-0 sm:pt-0 sm:border-t-0">
            {expiryStatus && expiryStatus !== "valid" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex-1 sm:flex-none h-10 sm:h-9 sm:w-9 px-3 sm:px-0 gap-1.5 border border-border sm:border-0" data-testid={`reminder-menu-${serial.id}`}>
                    <Bell className="w-4 h-4" />
                    <span className="sm:hidden">Erinnerung</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onSendEmail(serial)} disabled={!smtpConfigured}>
                    <Mail className="w-4 h-4 mr-2" />
                    E-Mail senden
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDownloadCalendar(serial)}>
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    Termin erstellen (ICS)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" className="flex-1 sm:flex-none h-10 sm:h-9 sm:w-9 px-3 sm:px-0 gap-1.5 border border-border sm:border-0" onClick={() => onEdit(serial)} data-testid={`edit-serial-${serial.id}`}>
              <Edit className="w-4 h-4" />
              <span className="sm:hidden">Bearbeiten</span>
            </Button>
            <Button
              variant="ghost"
              className="flex-1 sm:flex-none h-10 sm:h-9 sm:w-9 px-3 sm:px-0 gap-1.5 border border-border sm:border-0 text-destructive hover:text-destructive"
              onClick={() => onDelete(serial)}
              data-testid={`delete-serial-${serial.id}`}
            >
              <Trash2 className="w-4 h-4" />
              <span className="sm:hidden">Löschen</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
